// Media sharing ("check out this movie/show"). Distinct from coin sharing
// ("look what I earned"), which keeps its own flow and copy.
//
// Shared links reuse the canonical, public Media Detail routes
// (/movie/movie/:id, /movie/tv/:id, /movie/tv/:id/season/:n) — the same paths
// the web app, SEO pre-render, AASA and Android App Links already serve — so
// there is no share-only route. The UTM tag is what first-touch attribution
// already reads on both clients; it carries no user or account identifiers.
import { APP_DEEP_LINK_DESTINATIONS, ALUVO_WEB_ORIGIN, buildCanonicalWebUrl } from './deepLinks';

export const MEDIA_SHARE_SOURCE = 'media_share';

export const MEDIA_SHARE_UTM = Object.freeze({
    utm_source: 'aluvo',
    utm_medium: 'share',
    utm_campaign: MEDIA_SHARE_SOURCE,
});

export const MEDIA_SHARE_METHODS = Object.freeze({
    NATIVE_SHARE: 'native_share',
    WEB_SHARE: 'web_share',
    COPY_LINK: 'copy_link',
    // The share-image builder's PNG (with the link as text where the app takes both).
    IMAGE: 'image',
});

function hasSeason(seasonNumber) {
    return seasonNumber !== null && seasonNumber !== undefined && seasonNumber !== '';
}

function mediaDestination({ kind, tmdbId, seasonNumber }) {
    if (kind === 'tv') {
        return hasSeason(seasonNumber)
            ? { type: APP_DEEP_LINK_DESTINATIONS.TV_SEASON, params: { tmdbId, seasonNumber } }
            : { type: APP_DEEP_LINK_DESTINATIONS.TV, params: { tmdbId } };
    }
    if (kind === 'movie') return { type: APP_DEEP_LINK_DESTINATIONS.MOVIE, params: { tmdbId } };
    return null;
}

/** Canonical public URL for a movie, show or season (no tracking params). */
export function buildMediaUrl({ kind, tmdbId, seasonNumber = null }, origin = ALUVO_WEB_ORIGIN) {
    if (tmdbId === null || tmdbId === undefined || tmdbId === '') return null;
    const dest = mediaDestination({ kind, tmdbId, seasonNumber });
    return dest ? buildCanonicalWebUrl(dest.type, dest.params, origin) : null;
}

/** The URL handed to the share sheet: the canonical URL plus the media_share UTM tag. */
export function buildMediaShareUrl(media, origin = ALUVO_WEB_ORIGIN) {
    const url = buildMediaUrl(media, origin);
    if (!url) return null;
    return `${url}?${new URLSearchParams(MEDIA_SHARE_UTM).toString()}`;
}

/** "The Matrix (1999)", "Breaking Bad", "Attack on Titan — Season 1". */
export function buildMediaShareTitle({ kind, title, year = null, seasonNumber = null, seasonName = null }) {
    const name = String(title || '').trim() || 'this title';
    if (kind === 'tv' && hasSeason(seasonNumber)) {
        return `${name} — ${seasonName || `Season ${seasonNumber}`}`;
    }
    if (kind === 'movie' && year) return `${name} (${year})`;
    return name;
}

/** Short, neutral share copy without the URL (the caller decides where the link goes). */
export function buildMediaShareText(media) {
    const heading = buildMediaShareTitle(media);
    const body = media.kind === 'movie'
        ? 'Think you remember it?\nVerify what you watched on Aluvo.'
        : 'See it on Aluvo.';
    return `${heading}\n\n${body}`;
}

/** True when an incoming URL was produced by a media share. */
export function isMediaShareLink(input) {
    try {
        const url = input instanceof URL ? input : new URL(input, ALUVO_WEB_ORIGIN);
        return url.searchParams.get('utm_campaign') === MEDIA_SHARE_SOURCE;
    } catch {
        return false;
    }
}
